/**
 * Modal content for each perform category (Local Cameras, Media Laptops, Remote Speakers, OUTPUT, MEDIA).
 * Local cameras and media laptops support track selection. Remote speakers come on stage together via the
 * on-stage toggle.
 */

import { Box, Paper, Typography } from '@mui/material';
import type { FlowDto, FlowSlotDto } from '@/types/dtos/flows/response/flow.dto';
import type { LiveContextStateDto } from '@/state/queries/player/use-get-live-context-state';
import type { PerformCategory } from './perform-upper-buttons';
import { LiveKitPreviewConnector } from '@/components/preview/livekit-preview-connector';
import { LiveKitOutputConnector } from '@/components/preview/livekit-output-connector';
import { PerformCategoryTrackPicker } from './perform-category-track-picker';
import { PerformRemoteSpeakersPanel } from './perform-remote-speakers-panel';
import { useTranslations } from '@/i18n/use-translations';
import type { TranslationKey } from '@/i18n/translation-key.tsx';

export interface PerformCategoryModalContentProps {
  category: PerformCategory;
  flow: FlowDto;
  selectedSlot?: FlowSlotDto;
  entityRef?: string;
  eventSlotId?: string;
  livekitRoomId?: string;
  liveContextState?: LiveContextStateDto;
  onTrackSelected?: () => void;
}

const GRID_LAYOUT_SX = {
  'display': 'grid',
  'gridTemplateColumns': { xs: '1fr', md: 'repeat(2, 1fr)' },
  'gap': 2,
  'width': '100%',
  '& > *': {
    aspectRatio: '16/9',
    minHeight: 0,
    overflow: 'hidden',
    borderRadius: 1,
  },
} as const;

function PlaceholderMessage({ title, description }: { title: string; description: string }) {
  return (
    <Paper
      sx={{
        p: 3,
        textAlign: 'center',
        bgcolor: 'action.hover',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
      }}
    >
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>
    </Paper>
  );
}

export function PerformCategoryModalContent({
  category,
  flow,
  entityRef,
  eventSlotId,
  livekitRoomId,
  liveContextState,
  onTrackSelected,
}: PerformCategoryModalContentProps) {
  const t = useTranslations();

  if (category === 'cam' || category === 'remote') {
    return (
      <PerformCategoryTrackPicker
        category={category}
        flow={flow}
        entityRef={entityRef}
        eventSlotId={eventSlotId}
        livekitRoomId={livekitRoomId}
        liveContextState={liveContextState}
        onTrackSelected={onTrackSelected}
      />
    );
  }

  if (category === 'speakers') {
    return (
      <PerformRemoteSpeakersPanel
        flow={flow}
        entityRef={entityRef}
        eventSlotId={eventSlotId}
        livekitRoomId={livekitRoomId}
        liveContextState={liveContextState}
      />
    );
  }

  if (!livekitRoomId) {
    return (
      <Box sx={{ p: 2, color: 'text.secondary' }}>
        {t('flows.detail.perform.categoryModal.noLiveContext' as TranslationKey, {
          defaultValue: 'Live context not available. Start the flow to connect.',
        })}
      </Box>
    );
  }

  switch (category) {
    case 'media':
      return (
        <Box sx={GRID_LAYOUT_SX}>
          <Box sx={{ bgcolor: '#000', width: '100%', height: '100%' }}>
            <LiveKitPreviewConnector
              livekitRoomId={livekitRoomId}
              mode="preview"
              trackSids={[]}
              showScreenshare
              height="100%"
            />
          </Box>
        </Box>
      );

    case 'output': {
      const layers = liveContextState?.playState?.layers ?? [];
      if (layers.length === 0) {
        return (
          <Box sx={GRID_LAYOUT_SX}>
            <Box>
              <PlaceholderMessage
                title="OUTPUT Views — No layers yet"
                description="Start the flow and transition to a slot to see the composed layer output (Stage, Room, Live, Remote)."
              />
            </Box>
          </Box>
        );
      }
      return (
        <Box sx={GRID_LAYOUT_SX}>
          <Box sx={{ bgcolor: '#000', width: '100%', height: '100%' }}>
            <LiveKitOutputConnector livekitRoomId={livekitRoomId} layers={layers} height="100%" />
          </Box>
        </Box>
      );
    }

    default:
      return null;
  }
}
